import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Phone, Mail, Loader2, Send, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { api, imgUrl, apiError } from "@/lib/api";
import { QUOTE_STATUSES, fmtDate } from "@/lib/constants";
import StatusBadge from "@/components/admin/StatusBadge";

export default function QuoteDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [reply, setReply] = useState({ subject: "Votre demande de devis — MARY SERVICES", message: "" });
  const [sending, setSending] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const load = () => api.get(`/admin/quotes/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Demande introuvable"));
  useEffect(() => { load(); }, [id]);

  const setStatus = async (status) => {
    try {
      await api.patch(`/admin/quotes/${id}`, { status });
      toast.success("Statut mis à jour");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      await api.patch(`/admin/quotes/${id}`, { note });
      setNote("");
      toast.success("Note ajoutée");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingNote(false);
    }
  };

  const sendReply = async () => {
    if (!reply.message.trim()) return;
    setSending(true);
    try {
      await api.post(`/admin/quotes/${id}/reply`, reply);
      toast.success("E-mail envoyé au client");
      setReply({ ...reply, message: "" });
      load();
    } catch (err) {
      toast.error(apiError(err, "Échec de l'envoi de l'e-mail."));
      load();
    } finally {
      setSending(false);
    }
  };

  if (!data) return <p className="text-sm text-muted2">Chargement…</p>;
  const { quote: q, emails } = data;

  const card = "rounded-2xl bg-white border border-line p-6";
  const inputCls = "w-full rounded-xl border border-line bg-cream px-4 py-2.5 text-sm text-ink placeholder:text-muted2 focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest";

  return (
    <div data-testid="admin-quote-detail">
      <Link to="/admin/demandes" className="inline-flex items-center gap-1.5 text-sm text-muted2 hover:text-forest transition-colors" data-testid="quote-back">
        <ArrowLeft className="h-4 w-4" /> Toutes les demandes
      </Link>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <h1 className="font-display font-semibold text-2xl sm:text-3xl text-ink">{q.name}</h1>
        <StatusBadge status={q.status} />
      </div>
      <p className="mt-1 text-sm text-muted2">Reçue le {fmtDate(q.created_at)}</p>

      <div className="mt-8 grid gap-5 lg:grid-cols-2 items-start">
        <div className="space-y-5">
          <section className={card} data-testid="quote-client-card">
            <h2 className="font-mono text-xs uppercase tracking-[0.18em] text-muted2">Client</h2>
            <p className="mt-3 font-semibold text-ink">{q.name}</p>
            <p className="text-sm text-muted2">{q.client_type === "professionnel" ? "Professionnel" : "Particulier"}</p>
            <div className="mt-4 space-y-2">
              <a href={`tel:${q.phone.replace(/\s/g, "")}`} data-testid="quote-phone-link" className="flex items-center gap-2.5 text-sm text-forest font-medium hover:text-pine">
                <Phone className="h-4 w-4" /> {q.phone}
              </a>
              <a href={`mailto:${q.email}`} data-testid="quote-email-link" className="flex items-center gap-2.5 text-sm text-forest font-medium hover:text-pine">
                <Mail className="h-4 w-4" /> {q.email}
              </a>
            </div>
          </section>

          <section className={card} data-testid="quote-request-card">
            <h2 className="font-mono text-xs uppercase tracking-[0.18em] text-muted2">Demande</h2>
            <p className="mt-3 text-sm font-semibold text-ink">{q.need_type}</p>
            {q.message ? (
              <p className="mt-2 text-sm text-body2 whitespace-pre-wrap leading-relaxed">{q.message}</p>
            ) : (
              <p className="mt-2 text-sm text-muted2 italic">Aucun message.</p>
            )}
          </section>

          <section className={card} data-testid="quote-photos-card">
            <h2 className="font-mono text-xs uppercase tracking-[0.18em] text-muted2">Photos du chantier ({q.photos?.length || 0})</h2>
            {q.photos?.length ? (
              <div className="mt-4 grid grid-cols-3 gap-2.5">
                {q.photos.map((p, i) => (
                  <button key={i} onClick={() => setLightbox(p)} data-testid={`quote-photo-${i}`} className="aspect-square rounded-xl overflow-hidden bg-sand">
                    <img src={imgUrl(p)} alt={`Photo ${i + 1}`} loading="lazy" className="h-full w-full object-cover hover:scale-105 transition-transform" />
                  </button>
                ))}
              </div>
            ) : (
              <p className="mt-4 flex items-center gap-2 text-sm text-muted2"><ImageOff className="h-4 w-4" /> Aucune photo jointe.</p>
            )}
          </section>
        </div>

        <div className="space-y-5">
          <section className={card} data-testid="quote-followup-card">
            <h2 className="font-mono text-xs uppercase tracking-[0.18em] text-muted2">Suivi</h2>
            <label className="mt-4 block text-xs font-semibold text-body2 mb-1.5">Statut</label>
            <select value={q.status} onChange={(e) => setStatus(e.target.value)} data-testid="quote-status-select" className={inputCls}>
              {QUOTE_STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>

            <label className="mt-5 block text-xs font-semibold text-body2 mb-1.5">Notes internes (jamais visibles par le client)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Ex. : Appelé le 14/09 — souhaite intervention semaine prochaine." data-testid="quote-note-input" className={`${inputCls} resize-none`} />
            <button onClick={addNote} disabled={savingNote || !note.trim()} data-testid="quote-note-add" className="mt-2.5 rounded-full bg-sand text-forest text-xs font-semibold px-4 py-2 hover:bg-mint transition-colors disabled:opacity-50">
              Ajouter la note
            </button>
            {q.notes?.length > 0 && (
              <ul className="mt-4 space-y-2.5" data-testid="quote-notes-list">
                {[...q.notes].reverse().map((n, i) => (
                  <li key={i} className="rounded-xl bg-sand px-4 py-3">
                    <p className="text-sm text-body2">{n.text}</p>
                    <p className="mt-1 text-[11px] text-muted2">{fmtDate(n.at)}</p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className={card} data-testid="quote-reply-card">
            <h2 className="font-mono text-xs uppercase tracking-[0.18em] text-muted2">Répondre au client</h2>
            <p className="mt-2 text-xs text-muted2">Destinataire : <span className="font-medium text-body2">{q.email}</span></p>
            <input value={reply.subject} onChange={(e) => setReply({ ...reply, subject: e.target.value })} placeholder="Objet" data-testid="quote-reply-subject" className={`${inputCls} mt-3`} />
            <textarea value={reply.message} onChange={(e) => setReply({ ...reply, message: e.target.value })} rows={5} placeholder="Votre message…" data-testid="quote-reply-message" className={`${inputCls} mt-3 resize-none`} />
            <button onClick={sendReply} disabled={sending || !reply.message.trim()} data-testid="quote-reply-send" className="mt-3 w-full rounded-full bg-forest text-cream font-semibold py-3 text-sm hover:bg-pine transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sending ? "Envoi…" : "Envoyer"}
            </button>

            {emails.length > 0 && (
              <div className="mt-5" data-testid="quote-emails-history">
                <h3 className="text-xs font-semibold text-body2 mb-2">Historique des e-mails</h3>
                <ul className="space-y-2">
                  {emails.map((m) => (
                    <li key={m.id} className="rounded-xl border border-line px-4 py-3">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-ink truncate">{m.subject}</p>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${m.status === "envoye" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"}`}>
                          {m.status === "envoye" ? "Envoyé" : "Échec"}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-muted2">{fmtDate(m.sent_at)} — à {m.to}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        </div>
      </div>

      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="max-w-3xl p-2 bg-ink border-ink" data-testid="quote-lightbox">
          {lightbox && <img src={imgUrl(lightbox)} alt="Photo du chantier" className="w-full rounded-xl" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
